import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { testimonials as fallbackTestimonials } from "@/lib/testimonials";
import { Quote, Star, ArrowRight } from "lucide-react";

export default function Testimonials() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/page-config/testimonials").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const c = cfg || {};
  const hero = c.hero || {}, cta = c.cta || {};
  const testimonials = Array.isArray(c.items) && c.items.length ? c.items : fallbackTestimonials;
  useSEO({
    title: "Athlete Reviews — Online Boxing Coach Testimonials | Better Through Boxing",
    description: "Read what real athletes say about training with Coach Chris over Zoom. Reviews of virtual boxing lessons, the BTB Basics Program, and online boxing coaching for beginners and beyond.",
    path: "/testimonials",
    keywords: ["online boxing coach reviews", "virtual boxing lessons testimonials", "zoom boxing coaching reviews", "boxing coach for beginners reviews", "Better Through Boxing testimonials"],
  });

  // Inject AggregateRating JSON-LD for rich-results
  useEffect(() => {
    const existing = document.getElementById("testimonials-jsonld");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "testimonials-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://betterthroughboxing.com#business",
      legalName: "The Flow State Method LLC",
      name: "The Flow State Method LLC",
      alternateName: ["Better Through Boxing", "Flow State Method", "BTB"],
      brand: { "@type": "Brand", name: "Better Through Boxing" },
      url: "https://betterthroughboxing.com",
      description: "The Flow State Method LLC, doing business as Better Through Boxing — online boxing coaching with live virtual boxing lessons over Zoom by Coach Chris Persaud.",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        reviewCount: testimonials.length,
        bestRating: "5",
        worstRating: "5",
      },
      review: testimonials.map((t) => ({
        "@type": "Review",
        author: { "@type": "Person", name: t.author },
        reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
        reviewBody: t.quote,
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [testimonials]);

  return (
    <div className="App relative">
      <SiteHeader />
      <main data-testid="testimonials-page">
        {/* Hero */}
        {hero.enabled !== false && (
        <section className="border-b border-[#27272A]">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-4">{hero.kicker || "Reviews · 5.0 ★"}</div>
            <h1 className="font-display font-bold uppercase text-5xl md:text-7xl tracking-tight leading-[0.9] mb-6">
              {hero.title || "What athletes say."}
            </h1>
            <p className="text-zinc-300 leading-relaxed max-w-2xl mx-auto text-base md:text-lg whitespace-pre-line">
              {hero.body || "Real reviews from athletes who've trained with Coach Chris — over Zoom and in person. Beginners, returning fighters, competitors. Every quote is from someone who actually trained."}
            </p>
          </div>
        </section>
        )}

        {/* Featured + grid */}
        <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
          {/* Featured */}
          <div className="relative border-2 border-[#FF331F] bg-gradient-to-br from-[#FF331F]/15 via-[#0F0F0F] to-[#0F0F0F] p-8 md:p-14 mb-8 overflow-hidden" data-testid="featured-testimonial">
            <Quote className="absolute top-6 right-6 h-20 w-20 text-[#FF331F]/20" />
            <div className="flex gap-1 mb-6">
              {[1,2,3,4,5].map((i) => <Star key={i} className="h-5 w-5 fill-[#FF331F] text-[#FF331F]" />)}
            </div>
            <p className="font-display text-2xl md:text-3xl leading-snug text-white mb-6">
              &ldquo;{testimonials[0].quote}&rdquo;
            </p>
            <div className="text-xs uppercase tracking-[0.3em] text-[#FF331F] font-bold">— {testimonials[0].author}</div>
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.slice(1).map((t, i) => (
              <div key={i} className="border border-[#27272A] bg-[#0F0F0F] p-7 hover:border-[#FF331F]/50 transition-colors" data-testid={`testimonial-card-${i}`}>
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map((s) => <Star key={s} className="h-3.5 w-3.5 fill-[#FF331F] text-[#FF331F]" />)}
                </div>
                <p className="text-zinc-200 leading-relaxed mb-4 italic">&ldquo;{t.quote}&rdquo;</p>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold border-t border-[#27272A] pt-3">— {t.author}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        {cta.enabled !== false && (
        <section className="max-w-4xl mx-auto px-6 md:px-10 py-20 text-center border-t border-[#27272A]">
          <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-4">{cta.heading || "Add your story."}</h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-8 whitespace-pre-line">{cta.body || "Book a session, train with Coach Chris, and join the athletes above. The best review is the one you write after your first round."}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/book"><Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm h-12 px-6" data-testid="testimonials-cta-book">Book a session <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <Link to="/services"><Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest rounded-sm bg-transparent h-12 px-6">See services</Button></Link>
          </div>
        </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
